import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatLiksDateRangeLabel,
  type LiksDateDayRange,
} from '../../../components/liks/LiksDateRangeDialog/dateRangePicker';
import { useI18n } from '../../../i18n/useI18n';
import type {
  ChatbotRankingCriteria,
  LacTenantOverviewDateRange,
} from '../../../types/lac';
import { dayToIsoRange } from '../../../utils/dateTimeUtils';
import {
  ALL_TENANTS_FILTER,
  buildLacChatbotsSearchParams,
  LAC_CHATBOT_CRITERIA_OPTIONS,
  parseLacChatbotsFilterFromSearchParams,
} from '../lacChatbotsListParams';

type LacTenantOption = {
  name: string;
  displayName?: string | null;
};

type UseLacChatbotsSearchParamsOptions = {
  tenants: LacTenantOption[] | undefined;
};

export function useLacChatbotsSearchParams({
  tenants,
}: UseLacChatbotsSearchParamsOptions) {
  const { locale, translate } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);

  const filter = useMemo(
    () => parseLacChatbotsFilterFromSearchParams(searchParams),
    [searchParams],
  );

  const replaceParams = useCallback(
    ({
      tenant,
      criteria,
      fromDay,
      toDay,
    }: {
      tenant: string;
      criteria: ChatbotRankingCriteria;
      fromDay: string;
      toDay: string;
    }) => {
      setSearchParams(
        buildLacChatbotsSearchParams({
          tenant,
          criteria,
          fromDay,
          toDay,
        }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleTenantFilterChange = useCallback(
    (value: string) => {
      replaceParams({
        tenant: value,
        criteria: filter.criteria,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
      });
    },
    [filter.criteria, filter.fromDay, filter.toDay, replaceParams],
  );

  const handleCriteriaChange = useCallback(
    (value: string) => {
      if (!LAC_CHATBOT_CRITERIA_OPTIONS.some((option) => option === value)) {
        return;
      }
      replaceParams({
        tenant: filter.tenant,
        criteria: value as ChatbotRankingCriteria,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
      });
    },
    [filter.fromDay, filter.tenant, filter.toDay, replaceParams],
  );

  const handleApplyDateRange = useCallback(
    (range: LiksDateDayRange | null) => {
      replaceParams({
        tenant: filter.tenant,
        criteria: filter.criteria,
        fromDay: range?.fromDay ?? '',
        toDay: range?.toDay ?? '',
      });
      setIsDateDialogOpen(false);
    },
    [filter.criteria, filter.tenant, replaceParams],
  );

  const selectedDayRange = useMemo((): LiksDateDayRange | null => {
    if (
      filter.fromDay === '' ||
      filter.toDay === '' ||
      filter.toDay < filter.fromDay
    ) {
      return null;
    }
    return { fromDay: filter.fromDay, toDay: filter.toDay };
  }, [filter.fromDay, filter.toDay]);

  const dateRange = useMemo((): LacTenantOverviewDateRange | undefined => {
    if (selectedDayRange == null) {
      return undefined;
    }
    return dayToIsoRange(selectedDayRange.fromDay, selectedDayRange.toDay);
  }, [selectedDayRange]);

  const tenantOptions = useMemo(() => {
    const sortedTenants = [...(tenants ?? [])].sort((a, b) => {
      const labelA = (a.displayName?.trim() || a.name).toLocaleLowerCase(
        locale,
      );
      const labelB = (b.displayName?.trim() || b.name).toLocaleLowerCase(
        locale,
      );
      return labelA.localeCompare(labelB, locale);
    });

    return [
      {
        value: ALL_TENANTS_FILTER,
        label: translate('lac.chatbots.tenantFilter.all'),
      },
      ...sortedTenants.map((tenant) => ({
        value: tenant.name,
        label: tenant.displayName?.trim() || tenant.name,
      })),
    ];
  }, [locale, tenants, translate]);

  const criteriaOptions = useMemo(
    () =>
      LAC_CHATBOT_CRITERIA_OPTIONS.map((value) => ({
        value,
        label: translate(`lac.organisations.stats.chatbots.criteria.${value}`),
      })),
    [translate],
  );

  const selectedTenantName =
    filter.tenant !== ALL_TENANTS_FILTER &&
    tenantOptions.some((option) => option.value === filter.tenant)
      ? filter.tenant
      : undefined;

  const rangeLabel = formatLiksDateRangeLabel(
    selectedDayRange,
    locale,
    translate('common.dateRange.entireTime'),
  );

  return {
    tenantFilter: selectedTenantName ?? ALL_TENANTS_FILTER,
    handleTenantFilterChange,
    tenantOptions,
    criteria: filter.criteria,
    handleCriteriaChange,
    criteriaOptions,
    fromDay: filter.fromDay,
    toDay: filter.toDay,
    isDateDialogOpen,
    setIsDateDialogOpen,
    handleApplyDateRange,
    rangeLabel,
    dateRange,
    selectedTenantName,
  };
}
